import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Component, ElementRef, ViewChild, effect } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

import { ButtonModule } from 'primeng/button';
import { Table, TableModule } from 'primeng/table';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { MessageModule } from 'primeng/message';
import { ChartModule } from 'primeng/chart';
import { SelectModule } from 'primeng/select';

import * as XLSX from 'xlsx';

import { environment } from '../../../environments/environment';
import { CityOption, CityService } from '../../core/shared/city.service';
import { LocationSelectionService } from '../service/location-selection.service';

type ParsedRow = Record<string, any>;
type Severity = 'error' | 'warn' | 'info' | 'success';
type FileKey = 'inputs_time_series' | 'historical_data';
type PreviewType = 'inputs' | 'historical';
type GraphType = 'line' | 'bar';
type HistoricalSeriesType = 'Total' | 'Male' | 'Female';

type HistoricalMetricKey =
  | 'totalPopulation'
  | 'undiagnosed'
  | 'diagnosedNotTreated'
  | 'treatedNotControlled'
  | 'treatmentDropout'
  | 'controlled'
  | 'totalIncidenceOfCvd'
  | 'cvdDeathRate';

type IssueSource = 'frontend' | 'backend' | 'system';

type ValidationIssue = {
  severity: Severity;
  code: string;
  message: string;
  fileKey?: FileKey;
  variable?: string;
  year?: string;
  source?: IssueSource;
  backendKey?: string;
};

type ValidationIssueGroup = {
  severity: Severity;
  code: string;
  message: string;
  fileKey?: FileKey;
  source?: IssueSource;
  backendKey?: string;
  year?: string;
  variables: string[];
  displayVariables: string[];
  hiddenVariableCount: number;
  count: number;
};

type T3HistoricalApiResponse = {
  status: 'success' | 'fail';
  city?: string;
  city_name?: string;
  session_id?: string;
  years?: number[];
  historical_data?: Record<string, Record<string, number | null>>;
  message?: string;
  data?: {
    city_name?: string;
    session_id?: string;
    years?: number[];
    historical_data?: Record<string, Record<string, number | null>>;
    message?: string;
  };
};

type UploadedCalibrationFile = {
  key: FileKey;
  label: string;
  required: boolean;
  file: File | null;
  fileName: string;
  uploadedAt: string;
  fileSizeLabel: string;
  parsed: boolean;
  rows: ParsedRow[];
  columns: { field: string; header: string }[];
  issues: ValidationIssue[];
};

type HistoricalMetricConfig = {
  key: HistoricalMetricKey;
  label: string;
  male: string;
  female: string;
};

@Component({
  selector: 'app-file-upload',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    TableModule,
    CardModule,
    DialogModule,
    InputTextModule,
    TagModule,
    MessageModule,
    ChartModule,
    SelectModule
  ],
  templateUrl: './file-upload.component.html'
})
export class FileUploadComponent {
  @ViewChild('cityLevelFileInput') cityLevelFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('dtInputs') dtInputs!: Table;
  @ViewChild('dtHistorical') dtHistorical!: Table;

  readonly enableFrontendValidation = true;

  loading = false;
  errorMsg = '';
  successMsg = '';
  globalFilterValue = '';

  issuesDialogVisible = false;
  replaceDialogVisible = false;
  pendingReplacementFile: File | null = null;
  pendingReplacementIsCityLevel = false;

  activePreview: PreviewType = 'inputs';

  readonly defaultGraph1Type: GraphType = 'line';
  readonly defaultGraph2Type: GraphType = 'line';
  readonly defaultHistoricalGraphType: GraphType = 'line';

  graph1Type: GraphType = this.defaultGraph1Type;
  graph2Type: GraphType = this.defaultGraph2Type;
  historicalGraphType: GraphType = this.defaultHistoricalGraphType;

  graph1Data: any = null;
  graph1Options: any = null;
  graph2Data: any = null;
  graph2Options: any = null;
  historicalGraphData: any = null;
  historicalGraphOptions: any = null;

  cityLevelFile: File | null = null;
  cityLevelFileName = '';
  cityLevelUploadedAt = '';
  cityLevelFileSizeLabel = '';

  prefetchedHistoricalData: Record<string, Record<string, number | null>> | null = null;
  prefetchedHistoricalYears: string[] = [];
  prefetchedHistoricalCity = '';
  prefetchedHistoricalLoading = false;

  private lastHistoricalFetchCity = '';

  readonly historicalMetricConfigs: HistoricalMetricConfig[] = [
    {
      key: 'totalPopulation',
      label: 'Total Population',
      male: 'total hypertensive population[Male]',
      female: 'total hypertensive population[Female]'
    },
    {
      key: 'undiagnosed',
      label: 'Undiagnosed',
      male: 'undiagnosed hypertensive population[Male]',
      female: 'undiagnosed hypertensive population[Female]'
    },
    {
      key: 'diagnosedNotTreated',
      label: 'Diagnosed not treated',
      male: 'diagnosed not treated hypertensive population[Male]',
      female: 'diagnosed not treated hypertensive population[Female]'
    },
    {
      key: 'treatedNotControlled',
      label: 'Treated not Controlled',
      male: 'treated not controlled  hypertensive population[Male]',
      female: 'treated not controlled  hypertensive population[Female]'
    },
    {
      key: 'treatmentDropout',
      label: 'Treatment dropout',
      male: 'treatment dropout hypertensive population[Male]',
      female: 'treatment dropout hypertensive population[Female]'
    },
    {
      key: 'controlled',
      label: 'Controlled',
      male: 'controlled hypertensive population[Male]',
      female: 'controlled hypertensive population[Female]'
    },
    {
      key: 'totalIncidenceOfCvd',
      label: 'Total incidence of CVD',
      male: 'total incidence of CVD among (pre post CVD)[Male]',
      female: 'total incidence of CVD among (pre post CVD)[Female]'
    },
    {
      key: 'cvdDeathRate',
      label: 'CVD Death rate',
      male: 'CVD death rate[Male]',
      female: 'CVD death rate[Female]'
    }
  ];

  readonly historicalMetricOptions = this.historicalMetricConfigs.map(({ key, label }) => ({ label, value: key }));

  readonly historicalSeriesOptions = [
    { label: 'Total', value: 'Total' as HistoricalSeriesType },
    { label: 'Male', value: 'Male' as HistoricalSeriesType },
    { label: 'Female', value: 'Female' as HistoricalSeriesType }
  ];

  selectedHistoricalMetric: HistoricalMetricKey = 'totalPopulation';
  selectedHistoricalSeries: HistoricalSeriesType = 'Total';
  selectedHistoricalAggregateYear = '2011';

  readonly sampleTemplatePaths = {
    cityLevel: 'assets/T3-Excel-Sample/city_level_historical_data.csv'
  };

  readonly historicalRequiredVariables = this.historicalMetricConfigs.flatMap((m) => [m.male, m.female]);

  private readonly malePopulationVariables = [
    'data all population[Male, A4]',
    'data all population[Male, A9]',
    'data all population[Male, A14]',
    'data all population[Male, A19]',
    'data all population[Male, A24]',
    'data all population[Male, A29]',
    'data all population[Male, A34]',
    'data all population[Male, A39]',
    'data all population[Male, A44]',
    'data all population[Male, A49]',
    'data all population[Male, A54]',
    'data all population[Male, A59]',
    'data all population[Male, A64]',
    'data all population[Male, A69]',
    'data all population[Male, A74]',
    'data all population[Male, A79]',
    'data all population[Male, A84]',
    'data all population[Male, A89]',
    'data all population[Male, A94]'
  ];

  private readonly femalePopulationVariables = [
    'data all population[Female, A4]',
    'data all population[Female, A9]',
    'data all population[Female, A14]',
    'data all population[Female, A19]',
    'data all population[Female, A24]',
    'data all population[Female, A29]',
    'data all population[Female, A34]',
    'data all population[Female, A39]',
    'data all population[Female, A44]',
    'data all population[Female, A49]',
    'data all population[Female, A54]',
    'data all population[Female, A59]',
    'data all population[Female, A64]',
    'data all population[Female, A69]',
    'data all population[Female, A74]',
    'data all population[Female, A79]',
    'data all population[Female, A84]',
    'data all population[Female, A89]',
    'data all population[Female, A94]'
  ];

  files: UploadedCalibrationFile[] = [
    {
      key: 'inputs_time_series',
      label: 'Inputs Time Series',
      required: true,
      file: null,
      fileName: '',
      uploadedAt: '',
      fileSizeLabel: '',
      parsed: false,
      rows: [],
      columns: [],
      issues: []
    },
    {
      key: 'historical_data',
      label: 'Historical Data',
      required: true,
      file: null,
      fileName: '',
      uploadedAt: '',
      fileSizeLabel: '',
      parsed: false,
      rows: [],
      columns: [],
      issues: []
    }
  ];

  private readonly headers = new HttpHeaders({
    Authorization: `Bearer ${environment.handshakeToken}`
  });

  private readonly submitUrl = `${environment.apiUrl}/upload-file-t3`;

  constructor(
    private http: HttpClient,
    private citySvc: CityService,
    private locationSel: LocationSelectionService
  ) {
    effect(() => {
      const selected = this.locationSel.selected();
      const city = String(selected?.city || '').trim();

      if (!city || this.norm(city) === this.norm(this.lastHistoricalFetchCity)) {
        return;
      }

      this.lastHistoricalFetchCity = city;
      this.fetchPrefetchedHistoricalData(city);
    });
  }

  ngOnInit(): void {
    this.loadPreferredT3CityAndHistoricalData();
  }

  get inputsFile(): UploadedCalibrationFile {
    return this.files[0];
  }

  get historicalFile(): UploadedCalibrationFile {
    return this.files[1];
  }

  get blockingIssues(): ValidationIssue[] {
    return this.files.flatMap((file) => file.issues.filter((issue) => issue.severity === 'error'));
  }

  get warningIssues(): ValidationIssue[] {
    return this.files.flatMap((file) => file.issues.filter((issue) => issue.severity === 'warn'));
  }

  get canSubmit(): boolean {
    return !!this.cityLevelFile && this.inputsFile.parsed && this.historicalFile.parsed && this.blockingIssues.length === 0;
  }

  get uploadStatusCount(): number {
    return this.cityLevelFileName ? 1 : 0;
  }

  get cityLevelHasErrors(): boolean {
    return this.hasFileErrors('inputs_time_series') || this.hasFileErrors('historical_data');
  }

  get cityLevelHasWarnings(): boolean {
    return this.hasFileWarnings('inputs_time_series') || this.hasFileWarnings('historical_data');
  }

  get cityLevelStatusLabel(): string {
    if (!this.cityLevelFileName) return '';
    if (this.cityLevelHasErrors) return 'Needs review';
    if (this.cityLevelHasWarnings) return 'Warnings';
    return 'Completed';
  }

  get cityLevelStatusIcon(): string {
    if (!this.cityLevelFileName) return 'pi-upload';
    if (this.cityLevelHasErrors) return 'pi-exclamation-triangle';
    if (this.cityLevelHasWarnings) return 'pi-info-circle';
    return 'pi-check';
  }

  get inputFileVisibleIssues(): ValidationIssue[] {
    return this.inputsFile.issues.length
      ? this.inputsFile.issues
      : [{ severity: 'success', code: 'INPUTS_NO_ERRORS', message: 'No errors' }];
  }

  get historicalFileVisibleIssues(): ValidationIssue[] {
    return this.historicalFile.issues.length
      ? this.historicalFile.issues
      : [{ severity: 'success', code: 'HISTORICAL_NO_ERRORS', message: 'No errors' }];
  }

  get inputDialogIssues(): ValidationIssue[] {
    return this.inputsFile.issues.filter((issue) => issue.severity !== 'success');
  }

  get historicalDialogIssues(): ValidationIssue[] {
    return this.historicalFile.issues.filter((issue) => issue.severity !== 'success');
  }

  get inputDialogIssueGroups(): ValidationIssueGroup[] {
    return this.groupDialogIssues(this.inputDialogIssues);
  }

  get historicalDialogIssueGroups(): ValidationIssueGroup[] {
    return this.groupDialogIssues(this.historicalDialogIssues);
  }

  get hasAnyDialogIssues(): boolean {
    return this.inputDialogIssueGroups.length > 0 || this.historicalDialogIssueGroups.length > 0;
  }

  get inputErrorCount(): number {
    return this.inputDialogIssueGroups.filter((issue) => issue.severity === 'error').length;
  }

  get historicalErrorCount(): number {
    return this.historicalDialogIssueGroups.filter((issue) => issue.severity === 'error').length;
  }

  get inputWarningCount(): number {
    return this.inputDialogIssueGroups.filter((issue) => issue.severity === 'warn').length;
  }

  get historicalWarningCount(): number {
    return this.historicalDialogIssueGroups.filter((issue) => issue.severity === 'warn').length;
  }

  get hasHistoricalGraph(): boolean {
    return !!this.historicalGraphData && !!this.historicalFile.parsed;
  }

  get historicalAggregateYears(): string[] {
    return this.getHistoricalComparisonYears();
  }

  get activeHistoricalAggregateYear(): string {
    const years = this.historicalAggregateYears;

    if (this.selectedHistoricalAggregateYear === 'all') {
      return 'all';
    }

    if (years.includes(this.selectedHistoricalAggregateYear)) {
      return this.selectedHistoricalAggregateYear;
    }

    return years.includes('2011') ? '2011' : years[0] || '2011';
  }

  get historicalAggregates(): Array<{
    year: string;
    label: string;
    historicalData: string;
    uploadedData: string;
    difference: string;
    differenceValue: number;
  }> {
    const year = this.activeHistoricalAggregateYear === 'all' ? '2011' : this.activeHistoricalAggregateYear;

    return this.historicalMetricConfigs.map((metric) => {
      const historicalValue = this.getPrefetchedHistoricalMetricForYear(metric, 'Total', year);
      const uploadedValue = this.getUploadedHistoricalMetricForYear(metric, 'Total', year);
      const differenceValue = uploadedValue - historicalValue;

      return {
        year,
        label: metric.label,
        historicalData: this.formatNumber(historicalValue),
        uploadedData: this.formatNumber(uploadedValue),
        difference: this.formatNumber(differenceValue),
        differenceValue
      };
    });
  }

  get historicalAggregateAllYearRows(): Array<{
    label: string;
    comparisons: Array<{
      year: string;
      historicalData: string;
      uploadedData: string;
      difference: string;
      differenceValue: number;
    }>;
  }> {
    const years = this.historicalAggregateYears;

    return this.historicalMetricConfigs.map((metric) => ({
      label: metric.label,
      comparisons: years.map((year) => {
        const historicalValue = this.getPrefetchedHistoricalMetricForYear(metric, 'Total', year);
        const uploadedValue = this.getUploadedHistoricalMetricForYear(metric, 'Total', year);
        const differenceValue = uploadedValue - historicalValue;

        return {
          year,
          historicalData: this.formatNumber(historicalValue),
          uploadedData: this.formatNumber(uploadedValue),
          difference: this.formatNumber(differenceValue),
          differenceValue
        };
      })
    }));
  }

  get hasHistoricalAggregateRows(): boolean {
    return this.activeHistoricalAggregateYear === 'all'
      ? this.historicalAggregateAllYearRows.length > 0
      : this.historicalAggregates.length > 0;
  }

  get blockingIssueCountLabel(): string {
    return String(this.blockingIssues.length);
  }

  get warningIssueCountLabel(): string {
    return String(this.warningIssues.length);
  }

  setHistoricalAggregateYear(year: string): void {
    this.selectedHistoricalAggregateYear = year;
  }

  getDifferenceClass(value: number): string {
    if (value > 0) return 'text-rose-700 bg-rose-50';
    if (value < 0) return 'text-emerald-700 bg-emerald-50';
    return 'text-slate-600 bg-slate-50';
  }

  triggerCityLevelFilePicker(): void {
    this.cityLevelFileInput?.nativeElement?.click();
  }

  async onCityLevelFileSelected(event: Event): Promise<void> {
    this.errorMsg = '';
    this.successMsg = '';

    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0] || null;

    if (!file) return;

    const lower = file.name.toLowerCase();
    const validExtension = ['.csv', '.xlsx', '.xls'].some((extension) => lower.endsWith(extension));

    if (!validExtension) {
      this.errorMsg = `Invalid file type for "${file.name}". Only .csv, .xlsx and .xls are allowed.`;
      input.value = '';
      return;
    }

    if (!this.isExactCityLevelFileName(file.name)) {
      this.errorMsg = `Invalid file selected. Expected exact file name "city_level_historical_data" with .csv, .xlsx or .xls extension.`;
      input.value = '';
      return;
    }

    if (this.cityLevelFileName) {
      this.pendingReplacementFile = file;
      this.pendingReplacementIsCityLevel = true;
      this.replaceDialogVisible = true;
      input.value = '';
      return;
    }

    await this.processCityLevelFileUpload(file);
    input.value = '';
  }

  async confirmReplaceFile(): Promise<void> {
    if (!this.pendingReplacementFile) {
      this.replaceDialogVisible = false;
      return;
    }

    const file = this.pendingReplacementFile;
    const isCityLevel = this.pendingReplacementIsCityLevel;

    this.pendingReplacementFile = null;
    this.pendingReplacementIsCityLevel = false;
    this.replaceDialogVisible = false;

    if (isCityLevel) {
      this.clearCityLevelFile();
      await this.processCityLevelFileUpload(file);
    }
  }

  cancelReplaceFile(): void {
    this.pendingReplacementFile = null;
    this.pendingReplacementIsCityLevel = false;
    this.replaceDialogVisible = false;
  }

  clearAllFiles(): void {
    this.clearCityLevelFile();
  }

  clearCityLevelFile(): void {
    this.clearFile('inputs_time_series');
    this.clearFile('historical_data');

    this.cityLevelFile = null;
    this.cityLevelFileName = '';
    this.cityLevelUploadedAt = '';
    this.cityLevelFileSizeLabel = '';

    this.errorMsg = '';
    this.successMsg = '';
    this.globalFilterValue = '';
    this.activePreview = 'inputs';

    this.dtInputs?.clear();
    this.dtHistorical?.clear();

    try {
      if (this.cityLevelFileInput?.nativeElement) {
        this.cityLevelFileInput.nativeElement.value = '';
      }
    } catch {}
  }

  switchPreview(type: PreviewType): void {
    this.activePreview = type;
    this.globalFilterValue = '';
    this.dtInputs?.clear();
    this.dtHistorical?.clear();
  }

  applyGlobalFilter(event: Event): void {
    const value = (event.target as HTMLInputElement)?.value || '';
    this.globalFilterValue = value;

    (this.activePreview === 'inputs' ? this.dtInputs : this.dtHistorical)?.filterGlobal(value, 'contains');
  }

  openIssuesDialog(): void {
    this.issuesDialogVisible = true;
  }

  downloadCityLevelTemplate(): void {
    const link = document.createElement('a');

    link.href = this.sampleTemplatePaths.cityLevel;
    link.download = 'city_level_historical_data.csv';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  setGraph1Type(type: GraphType): void {
    this.graph1Type = type;
    this.buildInputsCharts();
  }

  setGraph2Type(type: GraphType): void {
    this.graph2Type = type;
    this.buildInputsCharts();
  }

  setHistoricalGraphType(type: GraphType): void {
    this.historicalGraphType = type;
    this.buildHistoricalGraph();
  }

  onHistoricalMetricChange(): void {
    this.buildHistoricalGraph();
  }

  onHistoricalSeriesChange(): void {
    this.buildHistoricalGraph();
  }

  hasFileErrors(fileKey: FileKey): boolean {
    return !!this.files.find((file) => file.key === fileKey)?.issues.some((issue) => issue.severity === 'error');
  }

  hasFileWarnings(fileKey: FileKey): boolean {
    return !!this.files.find((file) => file.key === fileKey)?.issues.some((issue) => issue.severity === 'warn');
  }

  getFileStatusLabel(fileKey: FileKey): string {
    const file = this.files.find((item) => item.key === fileKey);

    if (!file?.fileName) return '';
    if (this.hasFileErrors(fileKey)) return 'Needs review';
    if (this.hasFileWarnings(fileKey)) return 'Warnings';

    return 'Completed';
  }

  getFileStatusIcon(fileKey: FileKey): string {
    const file = this.files.find((item) => item.key === fileKey);

    if (!file?.fileName) return 'pi-upload';
    if (this.hasFileErrors(fileKey)) return 'pi-exclamation-triangle';
    if (this.hasFileWarnings(fileKey)) return 'pi-info-circle';

    return 'pi-check';
  }

  getCellErrorClass(fileKey: FileKey, rowData: ParsedRow, field: string): string {
    if (field === 'year') return '';

    return this.isCellInvalid(fileKey, rowData, field)
      ? 'bg-rose-50 text-rose-700 font-semibold ring-1 ring-rose-200'
      : '';
  }

  getCellErrorTitle(fileKey: FileKey, rowData: ParsedRow, field: string): string {
    if (field === 'year') return '';

    const variable = String(rowData?.['year'] ?? '').trim();

    const issue = this.files
      .find((file) => file.key === fileKey)
      ?.issues.find((item) => item.variable === variable && item.year === field);

    return issue?.message || '';
  }

  async submitFiles(): Promise<void> {
    this.errorMsg = '';
    this.successMsg = '';

    if (!this.cityLevelFile) {
      this.errorMsg = 'Please upload city_level_historical_data file before submitting.';
      return;
    }

    if (!this.canSubmit) {
      this.errorMsg = 'Please resolve blocking validation errors before submitting.';
      return;
    }

    this.loading = true;

    try {
      const backendIssues = await this.submitCityLevelFile();

      if (backendIssues.length > 0) {
        this.errorMsg = `${backendIssues.length} backend validation error(s) found. Please review highlighted cells.`;
        this.openIssuesDialog();
      } else {
        this.clearSubmittedFilesAfterSuccess();
        this.successMsg = 'File upload completed successfully.';
      }
    } catch (error: any) {
      this.errorMsg = error?.message || 'Failed to submit file.';
    } finally {
      this.loading = false;
    }
  }

  private async processCityLevelFileUpload(file: File): Promise<void> {
    this.loading = true;
    this.errorMsg = '';
    this.successMsg = '';

    try {
      const parsedRows = await this.parseFile(file);

      if (!parsedRows.length) {
        throw new Error(`The uploaded file "${file.name}" is empty or could not be parsed.`);
      }

      const allColumns = this.buildColumns(parsedRows);
      const { inputRows, historicalRows } = this.splitCityLevelRows(parsedRows, allColumns);

      if (!inputRows.length) {
        throw new Error('No Inputs Time Series rows were found in city_level_historical_data.');
      }

      if (!historicalRows.length) {
        throw new Error('No Historical Data rows were found in city_level_historical_data.');
      }

      const inputColumns = this.buildColumns(inputRows);
      const historicalColumns = this.buildColumns(historicalRows);

      const inputIssues = this.enableFrontendValidation
        ? this.validateInputsTimeSeries(inputRows, inputColumns, 'inputs_time_series')
        : [];

      const historicalIssues = this.enableFrontendValidation
        ? this.validateHistoricalData(historicalRows, historicalColumns)
        : [];

      this.cityLevelFile = file;
      this.cityLevelFileName = file.name;
      this.cityLevelUploadedAt = new Date().toLocaleString();
      this.cityLevelFileSizeLabel = this.formatBytes(file.size);

      Object.assign(this.inputsFile, {
        file,
        fileName: file.name,
        uploadedAt: this.cityLevelUploadedAt,
        fileSizeLabel: this.cityLevelFileSizeLabel,
        parsed: true,
        rows: inputRows,
        columns: inputColumns,
        issues: inputIssues
      });

      Object.assign(this.historicalFile, {
        file,
        fileName: file.name,
        uploadedAt: this.cityLevelUploadedAt,
        fileSizeLabel: this.cityLevelFileSizeLabel,
        parsed: true,
        rows: historicalRows,
        columns: historicalColumns,
        issues: historicalIssues
      });

      this.buildInputsCharts();
      this.buildHistoricalGraph();

      this.activePreview = 'inputs';
    } catch (error: any) {
      this.clearCityLevelFile();
      this.errorMsg = error?.message || `Failed to read uploaded file: ${file.name}`;
    } finally {
      this.loading = false;
    }
  }

  private splitCityLevelRows(
    rows: ParsedRow[],
    columns: { field: string; header: string }[]
  ): { inputRows: ParsedRow[]; historicalRows: ParsedRow[] } {
    const columnKeys = columns.map((column) => column.field);
    const firstColumn = columnKeys.includes('year') ? 'year' : columnKeys[0];

    const historicalSet = new Set(
      this.historicalRequiredVariables.map((value) => this.normalizeHistoricalVariable(value))
    );

    const historicalRows: ParsedRow[] = [];
    const inputRows: ParsedRow[] = [];

    for (const row of rows) {
      const variable = String(row[firstColumn] ?? '').trim();
      const normalized = this.normalizeHistoricalVariable(variable);

      if (!variable) continue;

      if (historicalSet.has(normalized)) {
        historicalRows.push(row);
      } else {
        inputRows.push(row);
      }
    }

    return { inputRows, historicalRows };
  }

  private async submitCityLevelFile(): Promise<ValidationIssue[]> {
    if (!this.cityLevelFile) return [];

    this.removeBackendIssues('inputs_time_series');
    this.removeBackendIssues('historical_data');

    const formData = new FormData();

    formData.append('file', this.cityLevelFile, this.cityLevelFile.name);
    formData.append(
      'json_file',
      new Blob([JSON.stringify({ city: this.getSelectedCity() })], { type: 'application/json' }),
      'payload.json'
    );

    const response = await firstValueFrom(this.http.post<any>(this.submitUrl, formData, { headers: this.headers }));
    const historicalBackendIssues = this.parseBackendValidationIssues(response, 'historical_data');

    if (historicalBackendIssues.length) {
      this.historicalFile.issues = [...this.historicalFile.issues, ...historicalBackendIssues];
    }

    return historicalBackendIssues;
  }

  private clearSubmittedFilesAfterSuccess(): void {
    this.clearCityLevelFile();
  }

  private loadPreferredT3CityAndHistoricalData(): void {
    this.citySvc
      .getAll()
      .pipe(take(1))
      .subscribe({
        next: (cities) => {
          const availableCities = this.filterCitiesForDashboardT3(cities || []);
          const current = this.locationSel.getSelected();

          const currentIsValid =
            !!current?.city &&
            availableCities.some((city) => this.norm(city.city) === this.norm(current.city));

          if (currentIsValid) {
            if (this.norm(current.city) !== this.norm(this.lastHistoricalFetchCity)) {
              this.lastHistoricalFetchCity = current.city;
              this.fetchPrefetchedHistoricalData(current.city);
            }
            return;
          }

          const preferredCity = availableCities[0] || null;

          if (!preferredCity) {
            this.errorMsg = 'No T3 city access is available for historical data comparison.';
            return;
          }

          this.locationSel.setSelected({
            country: preferredCity.country,
            city: preferredCity.city,
            cityId: preferredCity.id,
            t0Available: preferredCity.availability.t0,
            t3Available: preferredCity.availability.t3,
            t6Available: preferredCity.availability.t6,
            raw: preferredCity.raw
          });
        },
        error: () => {
          this.errorMsg = 'Failed to load available cities for T3 historical data.';
        }
      });
  }

  private filterCitiesForDashboardT3(cities: CityOption[]): CityOption[] {
    if (this.hasAllAccess()) {
      return cities.filter((city) => city.availability.t3);
    }

    const allowedCities = this.readMappedCitiesForDashboardT3();

    return cities.filter((city) => city.availability.t3 && allowedCities.has(this.norm(city.city)));
  }

  private fetchPrefetchedHistoricalData(city: string): void {
    this.prefetchedHistoricalData = null;
    this.prefetchedHistoricalYears = [];
    this.prefetchedHistoricalCity = city;

    if (this.historicalFile.parsed) {
      this.buildHistoricalGraph();
    }

    const params = new HttpParams()
      .set('city', city)
      .set('session_id', this.ensureSessionId());

    this.prefetchedHistoricalLoading = true;

    this.http
      .get<T3HistoricalApiResponse>(`${environment.apiUrl}/historical_data_t3`, { params, headers: this.headers })
      .subscribe({
        next: (response) => {
          this.prefetchedHistoricalLoading = false;

          const normalized = this.normalizeT3HistoricalResponse(response);

          if (normalized.status !== 'success' || !normalized.historicalData) {
            this.prefetchedHistoricalData = null;
            this.prefetchedHistoricalYears = [];
            this.prefetchedHistoricalCity = city;

            console.warn(normalized.message || 'Failed to load T3 historical data.');

            if (this.historicalFile.parsed) {
              this.buildHistoricalGraph();
            }

            return;
          }

          this.prefetchedHistoricalData = normalized.historicalData;
          this.prefetchedHistoricalYears = normalized.years.length
            ? normalized.years
            : this.getSortedYearKeysFromApiHistorical(normalized.historicalData);

          this.prefetchedHistoricalCity = normalized.cityName || city;

          if (this.historicalFile.parsed) {
            this.buildHistoricalGraph();
          }
        },
        error: () => {
          this.prefetchedHistoricalLoading = false;
          this.prefetchedHistoricalData = null;
          this.prefetchedHistoricalYears = [];
          this.prefetchedHistoricalCity = '';

          console.warn('Failed to load T3 historical data.');

          if (this.historicalFile.parsed) {
            this.buildHistoricalGraph();
          }
        }
      });
  }

  private normalizeT3HistoricalResponse(response: T3HistoricalApiResponse | null | undefined): {
    status: 'success' | 'fail';
    cityName: string;
    years: string[];
    historicalData: Record<string, Record<string, number | null>> | null;
    message: string;
  } {
    const payload = response?.data || response;

    const historicalData = payload?.historical_data || response?.historical_data || null;

    const rawYears = Array.isArray(payload?.years)
      ? payload.years
      : Array.isArray(response?.years)
        ? response.years
        : [];

    return {
      status: response?.status || 'fail',
      cityName: payload?.city_name || response?.city_name || response?.city || '',
      years: rawYears.map(String),
      historicalData,
      message: payload?.message || response?.message || ''
    };
  }

  private buildInputsCharts(): void {
    const rows = this.inputsFile.rows || [];
    const yearKeys = this.getSortedYearKeys(rows);

    if (!rows.length || !yearKeys.length) {
      this.graph1Data = null;
      this.graph2Data = null;
      return;
    }

    const maleRows = this.malePopulationVariables.map((variable) => this.findRow(rows, variable)).filter(Boolean) as ParsedRow[];
    const femaleRows = this.femalePopulationVariables.map((variable) => this.findRow(rows, variable)).filter(Boolean) as ParsedRow[];

    if (!maleRows.length || !femaleRows.length) {
      this.graph1Data = null;
      this.graph2Data = null;
      return;
    }

    const malePopulationData: number[] = [];
    const femalePopulationData: number[] = [];
    const totalPopulationData: number[] = [];

    yearKeys.forEach((year) => {
      const maleTotal = maleRows.reduce((sum, row) => sum + this.toNumericValue(row[year]), 0);
      const femaleTotal = femaleRows.reduce((sum, row) => sum + this.toNumericValue(row[year]), 0);

      malePopulationData.push(maleTotal);
      femalePopulationData.push(femaleTotal);
      totalPopulationData.push(maleTotal + femaleTotal);
    });

    this.graph1Data = {
      labels: yearKeys,
      datasets: [this.chartDataset('Total Population', totalPopulationData, '#e11d48', this.graph1Type, true)]
    };

    this.graph2Data = {
      labels: yearKeys,
      datasets: [
        this.chartDataset('Male Population', malePopulationData, '#d946ef', this.graph2Type),
        this.chartDataset('Female Population', femalePopulationData, '#fb7185', this.graph2Type)
      ]
    };

    this.graph1Options = this.buildChartOptions();
    this.graph2Options = this.buildChartOptions();
  }

  private buildHistoricalGraph(): void {
    const rows = this.historicalFile.rows || [];
    const metric = this.historicalMetricConfigs.find((item) => item.key === this.selectedHistoricalMetric);

    if (!rows.length || !metric) {
      this.historicalGraphData = null;
      return;
    }

    const uploadedYears = this.getSortedYearKeys(rows);
    const apiYears = this.prefetchedHistoricalData
      ? this.prefetchedHistoricalYears.length
        ? this.prefetchedHistoricalYears
        : this.getSortedYearKeysFromApiHistorical(this.prefetchedHistoricalData)
      : [];

    const yearKeys = Array.from(new Set([...apiYears, ...uploadedYears]))
      .filter((year) => /^\d{4}$/.test(String(year)))
      .sort((a, b) => Number(a) - Number(b));

    if (!yearKeys.length) {
      this.historicalGraphData = null;
      return;
    }

    const uploadedData = this.getUploadedHistoricalSeries(metric, this.selectedHistoricalSeries, yearKeys);

    const datasets = [
      this.chartDataset(`${metric.label} Uploaded Data`, uploadedData, '#2563eb', this.historicalGraphType, false)
    ];

    if (this.prefetchedHistoricalData) {
      const historicalData = this.getPrefetchedHistoricalSeries(metric, this.selectedHistoricalSeries, yearKeys);

      datasets.unshift(
        this.chartDataset(
          `${metric.label} Historical Data`,
          historicalData,
          '#e11d48',
          this.historicalGraphType,
          this.historicalGraphType === 'line'
        )
      );
    }

    this.historicalGraphData = { labels: yearKeys, datasets };
    this.historicalGraphOptions = this.buildChartOptions();
  }

  private validateInputsTimeSeries(
    rows: ParsedRow[],
    columns: { field: string; header: string }[],
    fileKey: FileKey
  ): ValidationIssue[] {
    const issues: ValidationIssue[] = [];

    if (fileKey !== 'inputs_time_series') return issues;

    if (!rows.length) {
      return [{ severity: 'error', code: 'EMPTY_FILE', message: 'Inputs Time Series file contains no rows.', fileKey, source: 'frontend' }];
    }

    const columnKeys = columns.map((column) => column.field);

    if (!columnKeys.length) {
      return [{ severity: 'error', code: 'NO_HEADERS', message: 'No column headers were found in the file.', fileKey, source: 'frontend' }];
    }

    const firstColumn = columnKeys.includes('year') ? 'year' : columnKeys[0];
    const yearColumns = columnKeys.filter((column) => column !== firstColumn);

    if (String(firstColumn).trim().toLowerCase() !== 'year') {
      issues.push({
        severity: 'error',
        code: 'FIRST_COLUMN_HEADER_INVALID',
        message: `The first column header must be "year". Found "${firstColumn}".`,
        fileKey,
        source: 'frontend'
      });
    }

    if (!yearColumns.length) {
      issues.push({
        severity: 'error',
        code: 'YEAR_COLUMNS_MISSING',
        message: 'No year columns were detected after the first column.',
        fileKey,
        source: 'frontend'
      });
      return issues;
    }

    if (!yearColumns.every((year) => /^\d{4}$/.test(String(year).trim()))) {
      issues.push({
        severity: 'error',
        code: 'INVALID_YEAR_HEADERS',
        message: 'All year headers after the first column must be 4 digit years such as 2011, 2012, 2013.',
        fileKey,
        source: 'frontend'
      });
    }

    const yearValues = yearColumns.map(Number).filter(Number.isFinite).sort((a, b) => a - b);

    if (yearValues.length) {
      if (yearValues[0] !== 2011) {
        issues.push({
          severity: 'error',
          code: 'YEAR_START_INVALID',
          message: `Year columns must start from 2011. Found ${yearValues[0]}.`,
          fileKey,
          source: 'frontend'
        });
      }

      for (let index = 1; index < yearValues.length; index++) {
        if (yearValues[index] !== yearValues[index - 1] + 1) {
          issues.push({
            severity: 'error',
            code: 'YEAR_GAP_DETECTED',
            message: `Year columns must be continuous. Gap detected between ${yearValues[index - 1]} and ${yearValues[index]}.`,
            fileKey,
            source: 'frontend'
          });
          break;
        }
      }
    }

    const isFractionLikeVariable = (variable: string) =>
      ['age specific fertility rate[', 'gender ratio[', 'age specific gender death rate[', 'net migration fraction['].some((prefix) =>
        variable.toLowerCase().startsWith(prefix)
      );

    const isCountLikeVariable = (variable: string) =>
      ['data immigrants[', 'data emigrants[', 'data all population['].some((prefix) => variable.toLowerCase().startsWith(prefix)) ||
      ['data births', 'data deaths'].includes(variable.toLowerCase());

    for (const row of rows) {
      const variable = String(row[firstColumn] ?? '').trim();

      if (!variable) {
        issues.push({
          severity: 'error',
          code: 'BLANK_VARIABLE_NAME',
          message: 'A row with blank variable name was found in the first column.',
          fileKey,
          source: 'frontend'
        });
        continue;
      }

      let populated = 0;

      for (const year of yearColumns) {
        const raw = row[year];

        if (raw === '' || raw === null || raw === undefined) {
          issues.push({
            severity: 'error',
            code: 'EMPTY_CELL',
            message: `Variable "${variable}" has an empty value in year ${year}.`,
            fileKey,
            variable,
            year,
            source: 'frontend'
          });
          continue;
        }

        populated++;

        const value = Number(raw);

        if (!Number.isFinite(value)) {
          issues.push({
            severity: 'error',
            code: 'NON_NUMERIC_CELL',
            message: `Variable "${variable}" has a non numeric value in year ${year}.`,
            fileKey,
            variable,
            year,
            source: 'frontend'
          });
          continue;
        }

        if (isFractionLikeVariable(variable) && (value < 0 || value > 1)) {
          issues.push({
            severity: 'error',
            code: 'FRACTION_OUT_OF_RANGE',
            message: `Fraction or rate variable "${variable}" has value ${value} in year ${year}; expected range is 0 to 1.`,
            fileKey,
            variable,
            year,
            source: 'frontend'
          });
        }

        if (isCountLikeVariable(variable) && value < 0) {
          issues.push({
            severity: 'error',
            code: 'NEGATIVE_COUNT',
            message: `Count variable "${variable}" contains a negative value in year ${year}.`,
            fileKey,
            variable,
            year,
            source: 'frontend'
          });
        }
      }

      if (populated !== yearColumns.length) {
        issues.push({
          severity: 'error',
          code: 'INCOMPLETE_VARIABLE',
          message: `Variable "${variable}" is incomplete: ${populated}/${yearColumns.length} years populated.`,
          fileKey,
          variable,
          source: 'frontend'
        });
      }
    }

    if (!issues.some((issue) => issue.severity === 'error')) {
      issues.push({
        severity: 'success',
        code: 'VALIDATION_OK',
        message: 'Inputs Time Series file passed the current validation checks.',
        fileKey,
        source: 'frontend'
      });
    }

    return issues;
  }

  private validateHistoricalData(rows: ParsedRow[], columns: { field: string; header: string }[]): ValidationIssue[] {
    const issues: ValidationIssue[] = [];
    const fileKey: FileKey = 'historical_data';

    if (!rows.length) {
      return [{ severity: 'error', code: 'HISTORICAL_EMPTY_FILE', message: 'Historical Data file contains no rows.', fileKey, source: 'frontend' }];
    }

    const columnKeys = columns.map((column) => column.field);

    if (!columnKeys.length) {
      return [{ severity: 'error', code: 'HISTORICAL_NO_HEADERS', message: 'No column headers were found in the Historical Data file.', fileKey, source: 'frontend' }];
    }

    const firstColumn = columnKeys.includes('year') ? 'year' : columnKeys[0];
    const yearColumns = columnKeys.filter((column) => column !== firstColumn);

    if (String(firstColumn).trim().toLowerCase() !== 'year') {
      issues.push({
        severity: 'error',
        code: 'HISTORICAL_FIRST_COLUMN_INVALID',
        message: `The first column header in Historical Data must be "year". Found "${firstColumn}".`,
        fileKey,
        source: 'frontend'
      });
    }

    if (!yearColumns.length) {
      issues.push({
        severity: 'error',
        code: 'HISTORICAL_YEAR_COLUMNS_MISSING',
        message: 'No year columns were detected in Historical Data after the first column.',
        fileKey,
        source: 'frontend'
      });
      return issues;
    }

    if (yearColumns.some((year) => !/^\d{4}$/.test(String(year).trim()))) {
      issues.push({
        severity: 'error',
        code: 'HISTORICAL_INVALID_YEAR_HEADERS',
        message: 'All Historical Data year headers after the first column must be 4 digit years such as 2011, 2012, 2013.',
        fileKey,
        source: 'frontend'
      });
    }

    const variableNames = rows.map((row) => String(row[firstColumn] ?? '').trim());

    if (!variableNames.some(Boolean)) {
      issues.push({
        severity: 'error',
        code: 'HISTORICAL_NO_VARIABLE_NAMES',
        message: 'No variable names were detected in the first column of Historical Data.',
        fileKey,
        source: 'frontend'
      });
      return issues;
    }

    const uploadedSet = new Set(variableNames.map((value) => this.normalizeHistoricalVariable(value)));
    const expectedSet = new Set(this.historicalRequiredVariables.map((value) => this.normalizeHistoricalVariable(value)));

    const missing = this.historicalRequiredVariables.filter((value) => !uploadedSet.has(this.normalizeHistoricalVariable(value)));
    const extra = variableNames.filter((value) => value && !expectedSet.has(this.normalizeHistoricalVariable(value)));

    if (missing.length) {
      issues.push({
        severity: 'error',
        code: 'HISTORICAL_MISSING_VARIABLES',
        message: `Missing required historical variables: ${missing.slice(0, 10).join(', ')}${missing.length > 10 ? ' ...' : ''}`,
        fileKey,
        source: 'frontend'
      });
    }

    if (extra.length) {
      issues.push({
        severity: 'warn',
        code: 'HISTORICAL_EXTRA_VARIABLES',
        message: `Unexpected historical variables found: ${extra.slice(0, 10).join(', ')}${extra.length > 10 ? ' ...' : ''}`,
        fileKey,
        source: 'frontend'
      });
    }

    for (const row of rows) {
      const variable = String(row[firstColumn] ?? '').trim();

      if (!variable) {
        issues.push({
          severity: 'error',
          code: 'HISTORICAL_BLANK_VARIABLE_NAME',
          message: 'A row with blank variable name was found in Historical Data.',
          fileKey,
          source: 'frontend'
        });
        continue;
      }

      let populated = 0;

      for (const year of yearColumns) {
        const raw = row[year];

        if (raw === '' || raw === null || raw === undefined) {
          issues.push({
            severity: 'error',
            code: 'HISTORICAL_EMPTY_CELL',
            message: `Variable "${variable}" has an empty value in year ${year}.`,
            fileKey,
            variable,
            year,
            source: 'frontend'
          });
          continue;
        }

        populated++;

        if (!Number.isFinite(Number(raw))) {
          issues.push({
            severity: 'error',
            code: 'HISTORICAL_NON_NUMERIC_CELL',
            message: `Variable "${variable}" has a non numeric value in year ${year}.`,
            fileKey,
            variable,
            year,
            source: 'frontend'
          });
        }
      }

      if (populated !== yearColumns.length) {
        issues.push({
          severity: 'error',
          code: 'HISTORICAL_INCOMPLETE_VARIABLE',
          message: `Variable "${variable}" is incomplete: ${populated}/${yearColumns.length} years populated.`,
          fileKey,
          variable,
          source: 'frontend'
        });
      }
    }

    issues.push(...this.validateHistoricalConsistency(rows, firstColumn, yearColumns));

    if (!issues.some((issue) => issue.severity === 'error')) {
      issues.push({
        severity: 'success',
        code: 'HISTORICAL_VALIDATION_OK',
        message: 'Historical Data file passed the current validation checks.',
        fileKey,
        source: 'frontend'
      });
    }

    return issues;
  }

  private validateHistoricalConsistency(rows: ParsedRow[], firstColumn: string, yearColumns: string[]): ValidationIssue[] {
    const issues: ValidationIssue[] = [];
    const fileKey: FileKey = 'historical_data';
    const genders: Array<'Male' | 'Female'> = ['Male', 'Female'];

    for (const gender of genders) {
      const totalHyperVariable = `total hypertensive population[${gender}]`;

      const hyperComponentVariables = [
        `undiagnosed hypertensive population[${gender}]`,
        `diagnosed not treated hypertensive population[${gender}]`,
        `treated not controlled  hypertensive population[${gender}]`,
        `treatment dropout hypertensive population[${gender}]`,
        `controlled hypertensive population[${gender}]`
      ];

      const hyperVariablesToHighlight = [totalHyperVariable, ...hyperComponentVariables];

      const totalHyperRow = this.findHistoricalVariableRow(rows, firstColumn, totalHyperVariable);
      const hyperComponentRows = hyperComponentVariables.map((variable) => this.findHistoricalVariableRow(rows, firstColumn, variable));

      const cvdIncidenceVariable = `total incidence of CVD among (pre post CVD)[${gender}]`;
      const cvdDeathVariable = `CVD death rate[${gender}]`;

      const cvdIncidenceRow = this.findHistoricalVariableRow(rows, firstColumn, cvdIncidenceVariable);
      const cvdDeathRow = this.findHistoricalVariableRow(rows, firstColumn, cvdDeathVariable);

      for (const year of yearColumns) {
        if (totalHyperRow && hyperComponentRows.every(Boolean)) {
          const totalHyper = this.toNullableNumber(totalHyperRow[year]);
          const componentValues = hyperComponentRows.map((row) => this.toNullableNumber(row?.[year]));

          if (totalHyper !== null && componentValues.every((value) => value !== null)) {
            const componentTotal = componentValues.reduce((sum, value) => sum + Number(value), 0);

            if (componentTotal > totalHyper) {
              const message =
                `${gender} hypertension category total (${this.formatNumber(componentTotal)}) ` +
                `must not exceed total hypertensive population (${this.formatNumber(totalHyper)}) in year ${year}.`;

              for (const variable of hyperVariablesToHighlight) {
                issues.push({
                  severity: 'error',
                  code: 'HISTORICAL_HYPERTENSION_TOTAL_MISMATCH',
                  message,
                  fileKey,
                  variable,
                  year,
                  source: 'frontend'
                });
              }
            }
          }
        }

        if (cvdIncidenceRow && cvdDeathRow) {
          const cvdIncidence = this.toNullableNumber(cvdIncidenceRow[year]);
          const cvdDeath = this.toNullableNumber(cvdDeathRow[year]);

          if (cvdIncidence !== null && cvdDeath !== null && cvdDeath > cvdIncidence) {
            const message =
              `${gender} CVD death rate (${this.formatNumber(cvdDeath)}) ` +
              `must not be greater than total incidence of CVD (${this.formatNumber(cvdIncidence)}) in year ${year}.`;

            for (const variable of [cvdIncidenceVariable, cvdDeathVariable]) {
              issues.push({
                severity: 'error',
                code: 'HISTORICAL_CVD_DEATH_EXCEEDS_INCIDENCE',
                message,
                fileKey,
                variable,
                year,
                source: 'frontend'
              });
            }
          }
        }
      }
    }

    return issues;
  }

  private parseBackendValidationIssues(response: any, fileKey: FileKey): ValidationIssue[] {
    const forwardStatusCode = Number(response?.forward_status_code ?? 200);

    if (Number.isFinite(forwardStatusCode) && forwardStatusCode >= 400) {
      return [
        {
          severity: 'error',
          code: 'BACKEND_FORWARD_ERROR',
          message: response?.message || 'Backend file processing failed.',
          fileKey,
          source: 'backend'
        }
      ];
    }

    const forwardResponse = response?.forward_response;

    if (!forwardResponse) return [];

    let parsed: any = null;

    try {
      parsed = typeof forwardResponse === 'string' ? JSON.parse(forwardResponse) : forwardResponse;
    } catch {
      const text = String(forwardResponse || '').trim();

      if (!text || text.toLowerCase() === 'passed' || text.toLowerCase().includes('upload successful')) {
        return [];
      }

      return [
        {
          severity: 'error',
          code: 'BACKEND_RESPONSE_PARSE_ERROR',
          message: text,
          fileKey,
          source: 'backend'
        }
      ];
    }

    if (!parsed || typeof parsed !== 'object') return [];
    if (this.isBackendSuccessPayload(parsed)) return [];

    const errorKeys = Object.keys(parsed).filter((key) => !this.isIgnorableBackendKey(key, parsed[key]));

    return errorKeys.flatMap((backendKey) =>
      this.mapBackendKeyToIssues(
        backendKey,
        String(parsed[backendKey] || 'Backend validation error.'),
        fileKey
      )
    );
  }

  private isBackendSuccessPayload(parsed: any): boolean {
    if (!parsed || typeof parsed !== 'object') return true;

    const keys = Object.keys(parsed);
    if (!keys.length) return true;

    const message = String(parsed?.message || '').trim().toLowerCase();
    const status = String(parsed?.status || '').trim().toLowerCase();

    if (message.includes('upload successful')) return true;
    if (status === 'success' && keys.every((key) => this.isIgnorableBackendKey(key, parsed[key]))) return true;

    return keys.every((key) => this.isIgnorableBackendKey(key, parsed[key]));
  }

  private isIgnorableBackendKey(key: string, value: any): boolean {
    const normalizedKey = String(key || '').trim().toLowerCase();
    const normalizedValue = String(value || '').trim().toLowerCase();

    return (
      normalizedKey === 'filename' ||
      normalizedKey === 'message' ||
      normalizedKey === 'status' ||
      normalizedKey === 'city_name' ||
      normalizedKey === 'session_id' ||
      normalizedValue === 'upload successful' ||
      normalizedValue === 'passed' ||
      normalizedValue === 'success'
    );
  }

  private mapBackendKeyToIssues(backendKey: string, message: string, fileKey: FileKey): ValidationIssue[] {
    const match = /^(Male|Female)_(CVD|Hyper)_(\d{4})$/i.exec(backendKey);

    if (!match) {
      return [
        {
          severity: 'error',
          code: 'BACKEND_VALIDATION',
          message,
          fileKey,
          source: 'backend',
          backendKey
        }
      ];
    }

    const gender = this.toTitleCase(match[1]);
    const group = match[2].toLowerCase();
    const year = match[3];

    const variables =
      group === 'cvd'
        ? [
            `total incidence of CVD among (pre post CVD)[${gender}]`,
            `CVD death rate[${gender}]`
          ]
        : [
            `total hypertensive population[${gender}]`,
            `undiagnosed hypertensive population[${gender}]`,
            `diagnosed not treated hypertensive population[${gender}]`,
            `treated not controlled  hypertensive population[${gender}]`,
            `treatment dropout hypertensive population[${gender}]`,
            `controlled hypertensive population[${gender}]`
          ];

    return variables.map((variable) => ({
      severity: 'error',
      code: 'BACKEND_VALIDATION',
      message,
      fileKey,
      variable,
      year,
      source: 'backend',
      backendKey
    }));
  }

  private groupDialogIssues(issues: ValidationIssue[]): ValidationIssueGroup[] {
    const grouped = new Map<string, ValidationIssueGroup>();

    for (const issue of issues) {
      const key = [
        issue.fileKey || '',
        issue.severity,
        issue.code,
        issue.source || '',
        issue.backendKey || '',
        issue.year || '',
        issue.message
      ].join('||');

      const existing = grouped.get(key);

      if (existing) {
        existing.count++;

        if (issue.variable && !existing.variables.includes(issue.variable)) {
          existing.variables.push(issue.variable);
        }

        existing.displayVariables = existing.variables.slice(0, 6);
        existing.hiddenVariableCount = Math.max(0, existing.variables.length - existing.displayVariables.length);
        continue;
      }

      const variables = issue.variable ? [issue.variable] : [];

      grouped.set(key, {
        severity: issue.severity,
        code: issue.code,
        message: issue.message,
        fileKey: issue.fileKey,
        source: issue.source,
        backendKey: issue.backendKey,
        year: issue.year,
        variables,
        displayVariables: variables.slice(0, 6),
        hiddenVariableCount: 0,
        count: 1
      });
    }

    const order: Record<Severity, number> = {
      error: 0,
      warn: 1,
      info: 2,
      success: 3
    };

    return Array.from(grouped.values()).sort((a, b) => {
      const severitySort = order[a.severity] - order[b.severity];
      if (severitySort !== 0) return severitySort;

      const yearSort = Number(a.year || 0) - Number(b.year || 0);
      if (yearSort !== 0) return yearSort;

      return a.message.localeCompare(b.message);
    });
  }

  private clearFile(fileKey: FileKey): void {
    const target = this.files.find((file) => file.key === fileKey);
    if (!target) return;

    Object.assign(target, {
      file: null,
      fileName: '',
      uploadedAt: '',
      fileSizeLabel: '',
      parsed: false,
      rows: [],
      columns: [],
      issues: []
    });

    if (fileKey === 'inputs_time_series') {
      this.graph1Data = null;
      this.graph2Data = null;
    }

    if (fileKey === 'historical_data') {
      this.historicalGraphData = null;
    }
  }

  private removeBackendIssues(fileKey: FileKey): void {
    const target = this.files.find((file) => file.key === fileKey);
    if (!target) return;

    target.issues = target.issues.filter((issue) => issue.source !== 'backend');
  }

  private isCellInvalid(fileKey: FileKey, rowData: ParsedRow, field: string): boolean {
    const variable = String(rowData?.['year'] ?? '').trim();

    return !!this.files
      .find((file) => file.key === fileKey)
      ?.issues.some((issue) => issue.variable === variable && issue.year === field);
  }

  private getSelectedCity(): string {
    try {
      return JSON.parse(sessionStorage.getItem('selectedLocation') || 'null')?.city || '';
    } catch {
      return '';
    }
  }

  private hasAllAccess(): boolean {
    const fromFlag = String(sessionStorage.getItem('allAccessFromMe') || '').toLowerCase() === 'true';

    if (fromFlag) return true;

    try {
      const raw = sessionStorage.getItem('userMappingFromMe');
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed?.ALL === true;
    } catch {
      return false;
    }
  }

  private readMappedCitiesForDashboardT3(): Set<string> {
    try {
      const raw = sessionStorage.getItem('userMappingFromMe');
      const parsed = raw ? JSON.parse(raw) : {};
      const arr = parsed?.dashboard?.T3 || [];

      return new Set(
        Array.isArray(arr)
          ? arr.map((value: any) => this.norm(String(value || ''))).filter(Boolean)
          : []
      );
    } catch {
      return new Set();
    }
  }

  private getHistoricalComparisonYears(): string[] {
    const apiYears = this.prefetchedHistoricalData
      ? this.prefetchedHistoricalYears.length
        ? this.prefetchedHistoricalYears
        : this.getSortedYearKeysFromApiHistorical(this.prefetchedHistoricalData)
      : [];

    const uploadedYears = this.historicalFile.rows.length
      ? this.getSortedYearKeys(this.historicalFile.rows)
      : [];

    return Array.from(new Set([...apiYears, ...uploadedYears]))
      .filter((year) => /^\d{4}$/.test(String(year)))
      .sort((a, b) => Number(a) - Number(b));
  }

  private getPrefetchedHistoricalMetricForYear(metric: HistoricalMetricConfig, series: HistoricalSeriesType, year: string): number {
    if (!this.prefetchedHistoricalData) return 0;

    const male = this.prefetchedHistoricalData[metric.male] || {};
    const female = this.prefetchedHistoricalData[metric.female] || {};

    const maleValue = this.toNumericValue(male[year]);
    const femaleValue = this.toNumericValue(female[year]);

    if (series === 'Male') return maleValue;
    if (series === 'Female') return femaleValue;

    return maleValue + femaleValue;
  }

  private getUploadedHistoricalMetricForYear(metric: HistoricalMetricConfig, series: HistoricalSeriesType, year: string): number {
    const rows = this.historicalFile.rows || [];

    const maleRow = this.findRow(rows, metric.male);
    const femaleRow = this.findRow(rows, metric.female);

    const maleValue = this.toNumericValue(maleRow?.[year]);
    const femaleValue = this.toNumericValue(femaleRow?.[year]);

    if (series === 'Male') return maleValue;
    if (series === 'Female') return femaleValue;

    return maleValue + femaleValue;
  }

  private getPrefetchedHistoricalSeries(metric: HistoricalMetricConfig, series: HistoricalSeriesType, years: string[]): number[] {
    return years.map((year) => this.getPrefetchedHistoricalMetricForYear(metric, series, year));
  }

  private getUploadedHistoricalSeries(metric: HistoricalMetricConfig, series: HistoricalSeriesType, years: string[]): number[] {
    return years.map((year) => this.getUploadedHistoricalMetricForYear(metric, series, year));
  }

  private getSortedYearKeysFromApiHistorical(data: Record<string, Record<string, number | null>>): string[] {
    const allYears = Object.values(data || {}).flatMap((yearMap) => Object.keys(yearMap || {}));

    return Array.from(new Set(allYears))
      .filter((key) => /^\d{4}$/.test(String(key).trim()) && Number(key) >= 2011)
      .sort((a, b) => Number(a) - Number(b));
  }

  private chartDataset(label: string, data: number[], color: string, type: GraphType, fill = false): any {
    return {
      label,
      data,
      borderColor: color,
      backgroundColor: type === 'bar' ? this.withAlpha(color, 0.75) : this.withAlpha(color, 0.12),
      tension: 0.35,
      fill,
      pointBackgroundColor: color,
      pointBorderColor: '#ffffff',
      pointHoverRadius: 5,
      pointRadius: 4,
      borderWidth: 3
    };
  }

  private buildChartOptions(): any {
    return {
      responsive: false,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          display: true,
          labels: { color: '#475569', usePointStyle: true, boxWidth: 10, padding: 16 }
        },
        tooltip: {
          backgroundColor: '#0f172a',
          titleColor: '#ffffff',
          bodyColor: '#ffffff',
          cornerRadius: 10,
          padding: 12,
          callbacks: {
            label: (ctx: any) => `${ctx.dataset.label}: ${this.formatNumber(ctx.parsed?.y ?? ctx.parsed)}`
          }
        }
      },
      scales: {
        x: {
          ticks: { color: '#64748b' },
          grid: { color: 'rgba(148, 163, 184, 0.12)' }
        },
        y: {
          ticks: {
            color: '#64748b',
            callback: (value: any) => this.formatNumber(value)
          },
          grid: { color: 'rgba(148, 163, 184, 0.12)' }
        }
      }
    };
  }

  private async parseFile(file: File): Promise<ParsedRow[]> {
    if (file.name.toLowerCase().endsWith('.csv')) {
      return this.parseCsv(await file.text());
    }

    const workbook = XLSX.read(await this.readFileAsArrayBuffer(file), { type: 'array' });
    const firstSheetName = workbook.SheetNames?.[0];

    if (!firstSheetName) {
      throw new Error('No worksheet found in the uploaded file.');
    }

    return this.parseWorksheetAsObjects(workbook.Sheets[firstSheetName]);
  }

  private parseCsv(text: string): ParsedRow[] {
    const workbook = XLSX.read(text, { type: 'string' });
    const firstSheetName = workbook.SheetNames?.[0];

    if (!firstSheetName) {
      throw new Error('Could not parse CSV file.');
    }

    return this.parseWorksheetAsObjects(workbook.Sheets[firstSheetName]);
  }

  private parseWorksheetAsObjects(worksheet: XLSX.WorkSheet): ParsedRow[] {
    const matrix = XLSX.utils.sheet_to_json<any[]>(worksheet, { header: 1, raw: false, defval: '' });

    if (!Array.isArray(matrix) || !matrix.length) {
      throw new Error('The worksheet is empty.');
    }

    let headerRow = (matrix[0] || []).map((header) => String(header ?? '').trim());
    const dataRows = matrix.slice(1);

    if (headerRow.length > 1 && (!headerRow[0] || headerRow[0] === '') && /^\d{4}$/.test(headerRow[1])) {
      headerRow[0] = 'year';
    }

    if (headerRow.length > 0 && /^\d{4}$/.test(headerRow[0])) {
      headerRow = ['year', ...headerRow];
    }

    if (!headerRow.length) {
      throw new Error('No header row found in the worksheet.');
    }

    const rows: ParsedRow[] = [];

    for (const row of dataRows) {
      const normalizedRow = row.length === headerRow.length - 1 ? ['', ...row] : row;
      const hasData = normalizedRow.some((cell: any) => String(cell ?? '').trim() !== '');

      if (!hasData) continue;

      const obj: ParsedRow = {};

      for (let index = 0; index < headerRow.length; index++) {
        obj[headerRow[index]] = normalizedRow[index] ?? '';
      }

      rows.push(obj);
    }

    const first = rows[0];

    if (first && /^\d+(\.\d+)?$/.test(String(first['year'] ?? '').trim())) {
      return dataRows
        .filter((row) => row?.length)
        .map((row) => {
          const obj: ParsedRow = { year: row[0] ?? '' };

          for (let index = 1; index < headerRow.length; index++) {
            obj[headerRow[index]] = row[index - 1] ?? '';
          }

          return obj;
        });
    }

    return rows;
  }

  private buildColumns(rows: ParsedRow[]): { field: string; header: string }[] {
    const keys = Object.keys(rows[0] || {});
    const orderedKeys = [...keys.filter((key) => key === 'year'), ...keys.filter((key) => key !== 'year')];

    return orderedKeys.map((field) => ({
      field,
      header: field === 'year' ? 'Variable' : this.toHeader(field)
    }));
  }

  private getSortedYearKeys(rows: ParsedRow[]): string[] {
    const firstRow = rows[0] || {};

    return Object.keys(firstRow)
      .filter((key) => /^\d{4}$/.test(String(key).trim()) && Number(key) >= 2011)
      .sort((a, b) => Number(a) - Number(b));
  }

  private findRow(rows: ParsedRow[], variableName: string): ParsedRow | null {
    return rows.find((row) => String(row['year'] ?? '').trim() === variableName) ?? null;
  }

  private findHistoricalVariableRow(rows: ParsedRow[], firstColumn: string, variableName: string): ParsedRow | null {
    const target = this.normalizeHistoricalVariable(variableName);

    return rows.find((row) => {
      const current = this.normalizeHistoricalVariable(String(row[firstColumn] ?? ''));
      return current === target;
    }) ?? null;
  }

  private toNullableNumber(value: any): number | null {
    if (value === '' || value === null || value === undefined) return null;

    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
  }

  private toNumericValue(value: any): number {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : 0;
  }

  private normalizeHistoricalVariable(value: string): string {
    return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
  }

  private isExactCityLevelFileName(fileName: string): boolean {
    const baseName = String(fileName || '')
      .trim()
      .toLowerCase()
      .replace(/\.(csv|xlsx|xls)$/i, '');

    return baseName === 'city_level_historical_data';
  }

  private toHeader(key: string): string {
    return String(key)
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (match) => match.toUpperCase());
  }

  private withAlpha(hex: string, alpha: number): string {
    const clean = hex.replace('#', '');
    const full = clean.length === 3 ? clean.split('').map((character) => character + character).join('') : clean;
    const [r, g, b] = [0, 2, 4].map((index) => parseInt(full.slice(index, index + 2), 16));

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private toTitleCase(value: string): string {
    const normalized = String(value || '').trim().toLowerCase();
    return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : '';
  }

  private norm(value: string): string {
    return String(value || '').trim().toLowerCase();
  }

  private ensureSessionId(): string {
    const key = 'session_id';
    let id = sessionStorage.getItem(key) || '';

    if (!id) {
      id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

      try {
        sessionStorage.setItem(key, id);
      } catch {}
    }

    return id;
  }

  private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(new Error('Unable to read file.'));

      reader.readAsArrayBuffer(file);
    });
  }

  private formatBytes(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';

    const units = ['B', 'KB', 'MB', 'GB'];
    let index = 0;
    let value = bytes;

    while (value >= 1024 && index < units.length - 1) {
      value /= 1024;
      index++;
    }

    return `${value.toFixed(1)} ${units[index]}`;
  }

  private formatNumber(value: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(Number(value || 0));
  }
}